import * as XLSX from "xlsx";

export const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "completed", label: "Completed" },
  { value: "in_progress", label: "In progress" },
  { value: "pending", label: "Pending" },
  { value: "draft", label: "Draft" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "cancelled", label: "Cancelled" },
];

export const BAND_OPTIONS = [
  { value: "", label: "All bands" },
  { value: "90-100", label: "90-100" },
  { value: "80-89", label: "80-89" },
  { value: "70-79", label: "70-79" },
  { value: "60-69", label: "60-69" },
  { value: "<60", label: "<60" },
];

export const RECOMMENDED_ACTION_OPTIONS = [
  { value: "", label: "All recommendations" },
  { value: "promotion", label: "Promotion" },
  { value: "increment", label: "Increment" },
  { value: "bonus", label: "Bonus" },
  { value: "none", label: "None" },
];

export const RECOMMENDATION_STATUS_OPTIONS = [
  { value: "", label: "Any recommendation status" },
  { value: "proposed", label: "Proposed" },
  { value: "revised", label: "Revised" },
  { value: "approved", label: "Approved" },
  { value: "withdrawn", label: "Withdrawn" },
  { value: "rejected", label: "Rejected" },
];

export const DEFAULT_FILTERS = {
  from: "",
  to: "",
  cycle_id: "",
  department_id: "",
  project_id: "",
  status: "",
  band: "",
  recommended_action: "",
  recommendation_status: "",
  delta_direction: "",
};

export const FINALIZE_ALLOWED_ROLES = ["hr_admin", "ceo"];

export const safeMsg = (error, fallback = "Something went wrong") =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

export const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

export const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString();
};

export const formatDateTime = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString();
};

export const formatPercent = (value, digits = 1) => {
  const n = toFiniteNumber(value);
  if (n === null) return "-";
  return `${n.toFixed(digits)}%`;
};

export const toRatingOver5 = (value) => {
  const n = toFiniteNumber(value);
  if (n === null) return null;
  if (n >= 0 && n <= 5) return Math.round(n * 100) / 100;
  return Math.round((n / 20) * 100) / 100;
};

export const formatRatingOver5 = (value, digits = 2) => {
  const n = toRatingOver5(value);
  if (n === null) return "-";
  return `${n.toFixed(digits)}/5`;
};

export const getFinalRatingOver5 = (item) =>
  toRatingOver5(
    item?.final_score_5 ??
      item?.final_score ??
      item?.final_rating ??
      item?.current_overall_rating ??
      item?.overall_rating ??
      item?.overall_details?.overall_rating ??
      item?.final_percentage
  );

export const getPreviousRatingOver5 = (item) =>
  toRatingOver5(
    item?.previous_cycle?.final_score_5 ??
      item?.previous_cycle?.final_score ??
      item?.previous_cycle?.final_rating ??
      item?.previous_overall_rating ??
      item?.previous_overall ??
      item?.prev_overall_rating ??
      item?.previous_cycle?.final_percentage
  );

export const titleCase = (value) => {
  const src = String(value || "").replace(/_/g, " ").trim();
  if (!src) return "-";
  return src
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

export const deriveBandFromPercentage = (percentage) => {
  const p = toFiniteNumber(percentage);
  if (p === null) return "<60";
  if (p >= 90) return "90-100";
  if (p >= 80) return "80-89";
  if (p >= 70) return "70-79";
  if (p >= 60) return "60-69";
  return "<60";
};

export const computeDelta = (item) => {
  const current = toFiniteNumber(item?.final_percentage);
  const previous = toFiniteNumber(item?.previous_cycle?.final_percentage);
  if (current === null || previous === null) return null;
  return Math.round((current - previous) * 10) / 10;
};

export const isFinalizeAllowed = (role) => FINALIZE_ALLOWED_ROLES.includes(String(role || "").toLowerCase());

export const getStatusColor = (status) => {
  const s = String(status || "").toLowerCase();
  if (["completed", "approved"].includes(s)) return "success";
  if (["in_progress", "pending"].includes(s)) return "warning";
  if (["rejected", "cancelled"].includes(s)) return "error";
  return "default";
};

export const getRecommendationStatusColor = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "approved") return "success";
  if (["proposed", "revised"].includes(s)) return "warning";
  if (["withdrawn", "rejected"].includes(s)) return "error";
  return "default";
};

export const sortByCreatedAtDesc = (rows = []) =>
  [...rows].sort((a, b) => new Date(b?.created_at || b?.changed_at || 0) - new Date(a?.created_at || a?.changed_at || 0));

export const getLatestRecommendation = (item) => {
  const rows = Array.isArray(item?.recommendations) ? item.recommendations : [];
  if (!rows.length) return null;
  return sortByCreatedAtDesc(rows)[0] || null;
};

export const getRecommendationSetBy = (item) => {
  const latest = getLatestRecommendation(item);
  if (!latest) return "-";
  const name = latest?.set_by?.name || "-";
  const role = titleCase(latest?.set_by?.role || "-");
  const step = titleCase(latest?.step_key || "-");
  return `${name} (${role}) - ${step}`;
};

export const getLatestRecommendationAction = (item) => {
  const latest = getLatestRecommendation(item);
  return latest?.action_type ? String(latest.action_type).toLowerCase() : "none";
};

export const parseBeforeAfter = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const normalizeCollection = (rows) => (Array.isArray(rows) ? rows : []);

export const normalizeReportResponse = (payload = {}) => {
  const summary = payload?.summary || {};
  const items = normalizeCollection(payload?.items).map((item) => {
    const employee = item?.employee || {};
    const project = item?.project || employee?.project || null;

    const previousCycle =
      item?.previous_cycle && typeof item.previous_cycle === "object"
        ? {
            ...item.previous_cycle,
            final_score_5:
              item.previous_cycle?.final_score_5 ??
              toRatingOver5(item.previous_cycle?.final_percentage),
          }
        : item?.previous_cycle;

    return {
      ...item,
      employee: {
        ...employee,
        position: employee?.position || employee?.position_name || employee?.job_title || employee?.title || "-",
      },
      project:
        project ||
        (employee?.project_id
          ? {
              id: employee.project_id,
              name: `Project #${employee.project_id}`,
            }
          : null),
      final_score_5: item?.final_score_5 ?? getFinalRatingOver5(item),
      previous_cycle: previousCycle,
    };
  });
  const lookups = payload?.lookups || {};
  const meta = payload?.meta || {};

  const total = Number(meta?.total ?? payload?.total ?? summary?.total ?? items.length ?? 0);
  const page = Number(meta?.current_page ?? payload?.current_page ?? 1);
  const perPage = Number((meta?.per_page ?? payload?.per_page ?? items.length) || 25);

  return {
    summary: {
      total: Number(summary?.total ?? items.length ?? 0),
      avg_score: toFiniteNumber(summary?.avg_score),
      completed_pct: toFiniteNumber(summary?.completed_pct),
      proposed_recommendations_count: Number(summary?.proposed_recommendations_count ?? 0),
      approved_recommendations_count: Number(summary?.approved_recommendations_count ?? 0),
      avg_delta_vs_previous: toFiniteNumber(summary?.avg_delta_vs_previous),
      by_band: normalizeCollection(summary?.by_band),
      by_recommended_action: normalizeCollection(summary?.by_recommended_action),
      by_status: normalizeCollection(summary?.by_status),
    },
    items,
    lookups: {
      departments: normalizeCollection(lookups?.departments),
      projects: normalizeCollection(lookups?.projects),
      cycles: normalizeCollection(lookups?.cycles),
      positions: normalizeCollection(lookups?.positions),
    },
    pagination: {
      total,
      page,
      per_page: perPage,
      hasServerMeta: Number.isFinite(total) && Number.isFinite(page) && Number.isFinite(perPage),
    },
    legacy: payload?._legacy || null,
  };
};

export const matchesLookupValue = (itemValue, filterValue) => {
  if (!filterValue) return true;
  if (itemValue === undefined || itemValue === null || itemValue === "") return false;
  return String(itemValue) === String(filterValue);
};

export const filterItemsClient = (items = [], filters = {}, search = "") => {
  const q = String(search || "").trim().toLowerCase();

  return items.filter((item) => {
    const employee = item?.employee || {};
    const project = item?.project || {};

    if (filters?.status && String(item?.status || "") !== String(filters.status)) return false;

    if (filters?.band && deriveBandFromPercentage(item?.final_percentage) !== filters.band) return false;

    if (filters?.recommended_action) {
      const latestAction = getLatestRecommendationAction(item);
      if (latestAction !== String(filters.recommended_action)) return false;
    }

    if (filters?.recommendation_status) {
      const rows = Array.isArray(item?.recommendations) ? item.recommendations : [];
      const hasStatus = rows.some(
        (r) => String(r?.status || "").toLowerCase() === String(filters.recommendation_status).toLowerCase()
      );
      if (!hasStatus) return false;
    }

    if (filters?.delta_direction) {
      const delta = computeDelta(item);
      if (filters.delta_direction === "improved" && !(delta > 0)) return false;
      if (filters.delta_direction === "declined" && !(delta < 0)) return false;
      if (filters.delta_direction === "same" && !(delta === 0)) return false;
    }

    if (!matchesLookupValue(item?.cycle_id || item?.cycle_name, filters?.cycle_id)) return false;
    if (!matchesLookupValue(employee?.department_id || employee?.department, filters?.department_id)) return false;
    if (!matchesLookupValue(project?.id || project?.name, filters?.project_id)) return false;

    if (filters?.from) {
      const from = new Date(filters.from);
      const submitted = new Date(item?.submitted_at || item?.last_updated_at || item?.updated_at || 0);
      if (!Number.isNaN(from.getTime()) && !Number.isNaN(submitted.getTime()) && submitted < from) return false;
    }

    if (filters?.to) {
      const to = new Date(filters.to);
      to.setHours(23, 59, 59, 999);
      const submitted = new Date(item?.submitted_at || item?.last_updated_at || item?.updated_at || 0);
      if (!Number.isNaN(to.getTime()) && !Number.isNaN(submitted.getTime()) && submitted > to) return false;
    }

    if (!q) return true;

    const hay = [
      employee?.name,
      employee?.employee_code,
      employee?.department,
      employee?.position,
      project?.name,
      item?.cycle_name,
      item?.status,
      item?.workflow_template,
      item?.last_step,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return hay.includes(q);
  });
};

export const computeSummaryFromItems = (items = []) => {
  const total = items.length;
  const sumScore = items.reduce((acc, item) => acc + (toFiniteNumber(item?.final_percentage) || 0), 0);
  const avgScore = total > 0 ? Math.round((sumScore / total) * 10) / 10 : 0;

  const byBandMap = {
    "90-100": 0,
    "80-89": 0,
    "70-79": 0,
    "60-69": 0,
    "<60": 0,
  };

  const byActionMap = {
    promotion: 0,
    increment: 0,
    bonus: 0,
    none: 0,
  };

  items.forEach((item) => {
    const band = deriveBandFromPercentage(item?.final_percentage);
    byBandMap[band] = (byBandMap[band] || 0) + 1;

    const action = getLatestRecommendationAction(item);
    byActionMap[action] = (byActionMap[action] || 0) + 1;
  });

  return {
    total,
    avg_score: avgScore,
    by_band: Object.entries(byBandMap).map(([band, count]) => ({ band, count })),
    by_recommended_action: Object.entries(byActionMap).map(([action, count]) => ({ action, count })),
  };
};

export const computeKpis = (items = []) => {
  const total = items.length;
  const summary = computeSummaryFromItems(items);

  const completedCount = items.filter((i) => {
    const status = String(i?.status || "").toLowerCase();
    return status === "completed" || status === "approved";
  }).length;

  const proposedRecommendationsCount = items.filter((i) =>
    (Array.isArray(i?.recommendations) ? i.recommendations : []).some((r) => {
      const status = String(r?.status || "").toLowerCase();
      return status === "proposed" || status === "revised";
    })
  ).length;

  const approvedRecommendationsCount = items.filter((i) =>
    (Array.isArray(i?.recommendations) ? i.recommendations : []).some(
      (r) => String(r?.status || "").toLowerCase() === "approved"
    )
  ).length;

  const deltas = items
    .map(computeDelta)
    .filter((d) => d !== null);

  const avgDelta = deltas.length
    ? Math.round((deltas.reduce((acc, v) => acc + v, 0) / deltas.length) * 10) / 10
    : 0;

  return {
    total,
    avg_score: summary.avg_score,
    avg_score_over_5: toRatingOver5(summary.avg_score),
    completed_pct: total > 0 ? Math.round((completedCount / total) * 1000) / 10 : 0,
    proposed_recommendations_count: proposedRecommendationsCount,
    approved_recommendations_count: approvedRecommendationsCount,
    avg_delta_vs_previous: avgDelta,
  };
};

export const computeStatusDistribution = (items = []) => {
  const map = new Map();
  items.forEach((item) => {
    const status = String(item?.status || "unknown");
    map.set(status, (map.get(status) || 0) + 1);
  });
  return Array.from(map.entries()).map(([status, count]) => ({ status, count }));
};

export const buildExportRows = (items = []) =>
  items.map((item) => {
    const latest = getLatestRecommendation(item);
    const delta = computeDelta(item);

    return {
      "Appraisal ID": item?.id,
      "Employee Code": item?.employee?.employee_code || "",
      Name: item?.employee?.name || "",
      Department: item?.employee?.department || "",
      Position: item?.employee?.position || "",
      Project: item?.project?.name || "",
      Cycle: item?.cycle_name || "",
      "Final %": toFiniteNumber(item?.final_percentage),
      "Final /5": getFinalRatingOver5(item),
      "Final Rating": item?.final_rating_label || "",
      "Previous Cycle": item?.previous_cycle?.cycle_name || "",
      "Previous %": toFiniteNumber(item?.previous_cycle?.final_percentage),
      "Previous /5": getPreviousRatingOver5(item),
      Delta: delta,
      Status: item?.status || "",
      "Latest Recommendation": latest?.action_type || "none",
      "Recommendation Status": latest?.status || "",
      "Recommendation By": latest?.set_by?.name || "",
      "Recommendation Role": latest?.set_by?.role || "",
      "Recommendation Step": latest?.step_key || "",
      "Last Step": item?.last_step || "",
      "Updated At": item?.last_updated_at || "",
    };
  });

const getExportFilename = (baseName) => `${baseName}_${new Date().toISOString().slice(0, 10)}`;

export const exportRowsToExcel = (rows = [], baseName = "appraisals_report") => {
  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
  XLSX.writeFile(workbook, `${getExportFilename(baseName)}.xlsx`);
};

export const exportRowsToCsv = (rows = [], baseName = "appraisals_report") => {
  const worksheet = XLSX.utils.json_to_sheet(rows);
  const csv = XLSX.utils.sheet_to_csv(worksheet);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${getExportFilename(baseName)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const pickRecommendationForPrefill = (item) => {
  const recommendations = Array.isArray(item?.recommendations) ? item.recommendations : [];
  if (!recommendations.length) return null;

  const ordered = sortByCreatedAtDesc(recommendations);

  const approvedHr = ordered.find((r) => {
    const isApproved = String(r?.status || "").toLowerCase() === "approved";
    const role = String(r?.set_by?.role || "").toLowerCase();
    const step = String(r?.step_key || "").toLowerCase();
    return isApproved && (role === "hr_admin" || step.includes("hr"));
  });

  if (approvedHr) return approvedHr;

  const latestProposed = ordered.find((r) => {
    const status = String(r?.status || "").toLowerCase();
    return status === "proposed" || status === "revised";
  });

  return latestProposed || ordered[0] || null;
};

export const buildFinalizePayloadItem = (item, actionType, config) => {
  const base = {
    appraisal_id: item?.id,
    employee_id: item?.employee?.id,
    percentage: toFiniteNumber(item?.final_percentage),
  };

  if (actionType === "promotion") {
    return {
      ...base,
      new_position_id: config?.new_position_id ? Number(config.new_position_id) : null,
    };
  }

  return {
    ...base,
    amount: toFiniteNumber(config?.amount),
  };
};

export const getBandColor = (band) => {
  if (band === "90-100") return "#2e7d32";
  if (band === "80-89") return "#1976d2";
  if (band === "70-79") return "#ed6c02";
  if (band === "60-69") return "#9c27b0";
  return "#d32f2f";
};

export const getActionColor = (action) => {
  const value = String(action || "none").toLowerCase();
  if (value === "promotion") return "#1565c0";
  if (value === "increment") return "#2e7d32";
  if (value === "bonus") return "#ef6c00";
  return "#616161";
};

export const getStatusPieColor = (status) => {
  const value = String(status || "").toLowerCase();
  if (["completed", "approved"].includes(value)) return "#2e7d32";
  if (["in_progress", "pending"].includes(value)) return "#ed6c02";
  if (["rejected", "cancelled"].includes(value)) return "#d32f2f";
  return "#546e7a";
};

export const formatCurrency = (value) => {
  const n = toFiniteNumber(value);
  if (n === null) return "-";
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};
